import { formatDate } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { gql, TypedDocumentNode } from 'apollo-angular';
import { OuiAutocomplete, OuiAutocompleteOption } from 'omnium-ui/form-field';
import { QueryManagerService } from 'projects/box-lib/src/lib/services/queryManagerService';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE } from '../../models/box-additionnal-data';
import {
  Investisseur,
  InvestisseurFilterInput,
  InvestisseursPaginatedCollectionSegment,
  InvestisseurType,
} from '../../models/generated/graphql';
import { AuthService } from '../../services/auth-service.service';

const allInvestisseursWithConsultant = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        creationDate
        statutCode
        ppeBackOffice
        gdaBackOffice
        statutCodeNavigation {
          libelle
          actif
        }
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
        investisseurConsultant(where: { actif: { eq: 1 } }) {
          consultant {
            personnePhysique {
              nom
              prenom
            }
          }
        }
      }
    }
  }
`;

const allInvestisseurPersonnesPhysiquesWithConsultant = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursPersonnesPhysiquesByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        creationDate
        statutCode
        ppeBackOffice
        gdaBackOffice
        statutCodeNavigation {
          libelle
          actif
        }
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
        investisseurConsultant(where: { actif: { eq: 1 } }) {
          consultant {
            personnePhysique {
              nom
              prenom
            }
          }
        }
      }
    }
  }
`;

const allInvestisseurPersonnesMoralesWithConsultant = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursPersonnesMoralesByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        creationDate
        statutCode
        ppeBackOffice
        gdaBackOffice
        statutCodeNavigation {
          libelle
          actif
        }
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
        investisseurConsultant(where: { actif: { eq: 1 } }) {
          consultant {
            personnePhysique {
              nom
              prenom
            }
          }
        }
      }
    }
  }
`;

const allInvestisseursBasic = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        ppeBackOffice
        gdaBackOffice
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
      }
    }
  }
`;

const allInvestisseursPersonnesPhysiquesBasic = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursPersonnesPhysiquesByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        ppeBackOffice
        gdaBackOffice
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
      }
    }
  }
`;

const allInvestisseursPersonnesMoralesBasic = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursPersonnesMoralesByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        ppeBackOffice
        gdaBackOffice
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
      }
    }
  }
`;

const allInvestisseursSouscription = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        creationDate
        statutCode
        ppeBackOffice
        gdaBackOffice
        statutCodeNavigation {
          libelle
          actif
        }
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
      }
    }
  }
`;

const allInvestisseursPersonnesPhysiquesSouscription = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursPersonnesPhysiquesByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        creationDate
        statutCode
        ppeBackOffice
        gdaBackOffice
        statutCodeNavigation {
          libelle
          actif
        }
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
      }
    }
  }
`;

const allInvestisseursPersonnesMoralesSouscription = gql`
  query allInvestisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursPersonnesMoralesByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        code
        creationDate
        statutCode
        ppeBackOffice
        gdaBackOffice
        statutCodeNavigation {
          libelle
          actif
        }
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
      }
    }
  }
`;

const allInvestisseursByCode = gql`
  query allInvestisseursByCode($filter: InvestisseurFilterInput!, $searchText: String!) {
    investisseursPaginated(skip: 0, take: 25, where: { and: [$filter, { code: { startsWith: $searchText } }] }) {
      items {
        id
        code
        creationDate
        statutCode
        ppeBackOffice
        gdaBackOffice
        statutCodeNavigation {
          libelle
          actif
        }
        investisseurEntite {
          displayName
          personnePhysique {
            dateNaissance
            prenom
            nom
            genre
          }
        }
        investisseurConsultant(where: { actif: { eq: 1 } }) {
          consultant {
            personnePhysique {
              nom
              prenom
            }
          }
        }
      }
    }
  }
`;

type Scope = 'all' | 'physiques' | 'morales';

type QueryKey =
  | 'investisseursByNamePaginated'
  | 'investisseursPersonnesPhysiquesByNamePaginated'
  | 'investisseursPersonnesMoralesByNamePaginated';

@Component({
    selector: 'app-search-investisseur-autocomplete',
    templateUrl: './search-investisseur-autocomplete.component.html',
    styleUrls: ['./search-investisseur-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchInvestisseurAutocompleteComponent implements AfterViewInit {
  @ViewChild('investisseurAutocomplete') investisseurAutocomplete: OuiAutocomplete<any>;

  @Input() label: string = 'Investisseur';
  @Input() initInvestisseur: Investisseur | undefined;
  @Input() isInvestisseurSet: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() isContactStatutExcluded: boolean = false;
  @Input() isClientStatutOnly: boolean = false;
  @Input() typePersonnes: InvestisseurType[] = [InvestisseurType.PersonneMorale, InvestisseurType.PersonnePhysique];
  @Input() fullDisplay: boolean = true;
  @Input() defaultQueryFilter: InvestisseurFilterInput;

  @Output() onInvestisseurIdSelected = new EventEmitter<number | null | undefined>();
  @Output() onInvestisseurPersonneSelected = new EventEmitter<Investisseur | undefined>();

  investisseurs: Investisseur[] = [];

  @Input() investisseurAutocompleteOptions: OuiAutocompleteOption<number>[] = [];
  @Input() investisseurControl = new FormControl<number | undefined>(undefined);

  isInvestisseurLoading: boolean = false;
  constructor(
    private queryManager: QueryManagerService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    if (this.initInvestisseur) {
      this.investisseurs = [this.initInvestisseur!];
      this.setOptions();
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    const statutContactChange = changes['isContactStatutExcluded'];
    if (statutContactChange && statutContactChange.previousValue !== undefined) {
      this.investisseurControl.setValue(undefined);
      this.onInvestisseurIdSelected.emit(null);
      this.onInvestisseurPersonneSelected.emit(undefined);
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.initInvestisseur) {
        this.investisseurControl.setValue(this.initInvestisseur.id);
      }

      if (this.isRequired) {
        this.investisseurControl.setValidators(Validators.required);
      }

      if (this.initInvestisseur && this.isInvestisseurSet) {
        this.investisseurControl.disable();
      }

      this.initSearchInvestisseurs();
    });
  }

  getScopeFromTypes(): Scope {
    const hasPhysique = this.typePersonnes?.includes(InvestisseurType.PersonnePhysique);
    const hasMorale = this.typePersonnes?.includes(InvestisseurType.PersonneMorale);

    if (hasPhysique && !hasMorale) return 'physiques';
    if (hasMorale && !hasPhysique) return 'morales';
    return 'all';
  }

  private getGraphQueryConfig(): { query: TypedDocumentNode<unknown, unknown>; key: QueryKey } {
    const scope = this.getScopeFromTypes();

    // Variante d'affichage
    const variant = !this.fullDisplay ? 'basic' : this.authService.isBackOffice() ? 'bo' : 'souscription';

    if (scope === 'physiques') {
      if (variant === 'basic')
        return {
          query: allInvestisseursPersonnesPhysiquesBasic,
          key: 'investisseursPersonnesPhysiquesByNamePaginated',
        };
      if (variant === 'bo')
        return {
          query: allInvestisseurPersonnesPhysiquesWithConsultant,
          key: 'investisseursPersonnesPhysiquesByNamePaginated',
        };
      return {
        query: allInvestisseursPersonnesPhysiquesSouscription,
        key: 'investisseursPersonnesPhysiquesByNamePaginated',
      };
    }

    if (scope === 'morales') {
      if (variant === 'basic')
        return { query: allInvestisseursPersonnesMoralesBasic, key: 'investisseursPersonnesMoralesByNamePaginated' };
      if (variant === 'bo')
        return {
          query: allInvestisseurPersonnesMoralesWithConsultant,
          key: 'investisseursPersonnesMoralesByNamePaginated',
        };
      return {
        query: allInvestisseursPersonnesMoralesSouscription,
        key: 'investisseursPersonnesMoralesByNamePaginated',
      };
    }

    // all
    if (variant === 'basic') return { query: allInvestisseursBasic, key: 'investisseursByNamePaginated' };
    if (variant === 'bo') return { query: allInvestisseursWithConsultant, key: 'investisseursByNamePaginated' };
    return { query: allInvestisseursSouscription, key: 'investisseursByNamePaginated' };
  }

  async initSearchInvestisseurs() {
    this.listenForInvestisseursChanges();

    if (!this.initInvestisseur) {
      this.fetchInvestisseurs();
    }
  }

  setOptions() {
    this.investisseurAutocompleteOptions = this.investisseurs.map(investisseur => ({
      label: this.formatInvestisseurLabel(investisseur),
      value: investisseur.id,
    }));
  }

  listenForInvestisseursChanges() {
    this.investisseurControl.valueChanges.pipe(debounceTime(350), distinctUntilChanged()).subscribe(investisseurId => {
      if (Number.isInteger(investisseurId)) {
        const investisseurFound = this.investisseurs.find(investisseur => investisseur.id == investisseurId);
        if (investisseurFound) {
          this.onInvestisseurIdSelected.emit(investisseurFound.id);
          this.onInvestisseurPersonneSelected.emit(investisseurFound);
        }
      } else {
        if (investisseurId) {
          this.fetchInvestisseurs(investisseurId.toString());
        } else {
          this.fetchInvestisseurs();
        }

        this.onInvestisseurIdSelected.emit(null);
        this.onInvestisseurPersonneSelected.emit(undefined);
      }
    });
  }

  async fetchInvestisseurs(search: string = '') {
    this.isInvestisseurLoading = true;
    const { query, key } = this.getGraphQueryConfig();
    const normalizedSearch = search.trim();
    let investisseurResult = await firstValueFrom(
      this.queryManager.query<Record<QueryKey, InvestisseursPaginatedCollectionSegment>>({
        query,
        variables: {
          filter: this.buildFilter(),
          searchText: normalizedSearch,
        },
      })
    );
    this.investisseurs = investisseurResult.data[key]?.items ?? [];

    if (normalizedSearch) {
      const byCodeResult = await firstValueFrom(
        this.queryManager.query<{ investisseursPaginated: InvestisseursPaginatedCollectionSegment }>({
          query: allInvestisseursByCode,
          variables: {
            filter: this.buildFilter(),
            searchText: normalizedSearch,
          },
        })
      );

      this.investisseurs = this.investisseurs.concat(byCodeResult.data.investisseursPaginated?.items ?? []);
      this.investisseurs = this.investisseurs.filter(
        (investisseur, index, arr) => arr.findIndex(inv => inv.id === investisseur.id) === index
      );
    }

    this.setOptions();
    this.isInvestisseurLoading = false;
  }

  buildFilter(): InvestisseurFilterInput {
    const personnePhysiqueFilter = { personnePhysique: { any: true } };
    const personneMoraleFilter = { personneMorale: { any: true } };
    //check type investisseur
    const personneFilter: InvestisseurFilterInput = { or: [] };
    if (this.typePersonnes.includes(InvestisseurType.PersonnePhysique)) {
      personneFilter.or!.push(personnePhysiqueFilter);
    }
    if (this.typePersonnes.includes(InvestisseurType.PersonneMorale)) {
      personneFilter.or!.push(personneMoraleFilter);
    }
    const investFilter: InvestisseurFilterInput = { and: [] };
    if (personneFilter.or!.length > 0) {
      investFilter.and!.push(personneFilter);
    }
    // check if contact need to be excluded
    const excludeContactStatutFilter = { statutCode: { neq: 'CONTACT' } };
    const clientStatutOnlyFilter = { statutCode: { eq: 'CLIENT' } };
    if (this.isContactStatutExcluded) {
      investFilter.and!.push(excludeContactStatutFilter);
    }
    if (this.isClientStatutOnly) {
      investFilter.and!.push(clientStatutOnlyFilter);
    }
    // add default filter
    if (this.defaultQueryFilter) {
      investFilter.and!.push(this.defaultQueryFilter);
    }
    return investFilter;
  }
  formatInvestisseurLabel(investisseur: Investisseur) {
    let label = '';

    label += investisseur?.investisseurEntite?.displayName ?? '';

    const investisseurPhysique = investisseur?.investisseurEntite?.personnePhysique;
    if (investisseurPhysique?.dateNaissance) {
      investisseur?.investisseurEntite?.personnePhysique?.genre == PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE
        ? (label += ' (née le ')
        : (label += ' (né le ');

      label +=
        formatDate(investisseur.investisseurEntite?.personnePhysique?.dateNaissance, 'dd/MM/yyyy', 'fr_FR') + ')';
    }

    if (investisseur.investisseurConsultant?.length > 0 && this.authService.isBackOffice()) {
      label +=
        ' - ' +
        (investisseur?.investisseurConsultant?.[0]?.consultant?.personnePhysique?.[0]?.nom ?? '') +
        ' ' +
        (investisseur?.investisseurConsultant?.[0]?.consultant?.personnePhysique?.[0]?.prenom ?? '');

      if (investisseur.investisseurConsultant?.length > 1) {
        label += ' (primaire), ';

        label +=
          (investisseur?.investisseurConsultant?.[1]?.consultant?.personnePhysique?.[0]?.nom || '') +
          ' ' +
          (investisseur?.investisseurConsultant?.[1]?.consultant?.personnePhysique?.[0]?.prenom || '');
        label += ' (secondaire)';
      }
    }

    investisseur.statutCodeNavigation?.actif &&
      (label += ' - ' + investisseur.statutCodeNavigation?.libelle.toLocaleUpperCase());

    investisseur?.code && (label += ' - ' + investisseur.code);
    return label;
  }

  setControlValue(investisseurId: number | null | undefined) {
    if (investisseurId) {
      this.investisseurControl.setValue(investisseurId);
    } else {
      this.investisseurControl.setValue(null);
      this.investisseurControl.reset();
      this.investisseurAutocomplete.empty();
    }
    this.investisseurControl.updateValueAndValidity();
  }

  private getGraphQuery(): TypedDocumentNode<unknown, unknown> {
    if (!this.fullDisplay) {
      return allInvestisseursBasic;
    }
    if (this.authService.isBackOffice()) {
      return allInvestisseursWithConsultant;
    }
    return allInvestisseursSouscription;
  }
  markAsTouched() {
    this.investisseurControl.markAsTouched();
    this.investisseurControl.updateValueAndValidity();
  }
}
